import store from "@/store";
import { performLogout } from "@/Shared/logout";

const DEFAULT_IDLE_LOGOUT_MINUTES = 5;
const configuredIdleLogoutMinutes = Number(process.env.VUE_APP_IDLE_LOGOUT_MINUTES);
const idleLogoutMinutes =
  Number.isFinite(configuredIdleLogoutMinutes) && configuredIdleLogoutMinutes > 0
    ? configuredIdleLogoutMinutes
    : DEFAULT_IDLE_LOGOUT_MINUTES;
const IDLE_TIMEOUT_MS = idleLogoutMinutes * 60 * 1000;
const LAST_ACTIVITY_KEY = "lastActivityAt";
const LOGOUT_EVENT_KEY = "idleLogoutAt";
const IDLE_LOGOUT_ALERT_KEY = "idleSessionExpired";
const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "click",
];

let idleTimeoutId: number | null = null;
let stopAuthWatch: (() => void) | null = null;
let isStarted = false;
let isLoggingOut = false;

function isAuthenticated(): boolean {
  return Boolean(store.getters.authenticated && store.getters.getIdToken);
}

function clearIdleTimeout(): void {
  if (idleTimeoutId !== null) {
    window.clearTimeout(idleTimeoutId);
    idleTimeoutId = null;
  }
}

function setLastActivity(timestamp = Date.now()): void {
  localStorage.setItem(LAST_ACTIVITY_KEY, timestamp.toString());
}

function getLastActivity(): number {
  const rawValue = localStorage.getItem(LAST_ACTIVITY_KEY);
  const timestamp = Number(rawValue);

  if (!Number.isFinite(timestamp) || timestamp <= 0) {
    return Date.now();
  }

  return timestamp;
}

function markIdleLogoutAlert(): void {
  sessionStorage.setItem(IDLE_LOGOUT_ALERT_KEY, "true");
}

async function triggerIdleLogout(audit = true): Promise<void> {
  if (isLoggingOut || !isAuthenticated()) {
    return;
  }

  isLoggingOut = true;
  markIdleLogoutAlert();

  try {
    await performLogout({ audit });
  } finally {
    isLoggingOut = false;
  }
}

function broadcastIdleLogout(): void {
  localStorage.setItem(LOGOUT_EVENT_KEY, Date.now().toString());
  void triggerIdleLogout();
}

function scheduleIdleTimeout(): void {
  clearIdleTimeout();

  if (!isAuthenticated()) {
    return;
  }

  const elapsedTime = Date.now() - getLastActivity();
  const remainingTime = IDLE_TIMEOUT_MS - elapsedTime;

  if (remainingTime <= 0) {
    broadcastIdleLogout();
    return;
  }

  idleTimeoutId = window.setTimeout(() => {
    broadcastIdleLogout();
  }, remainingTime);
}

function handleUserActivity(): void {
  if (!isAuthenticated()) {
    clearIdleTimeout();
    return;
  }

  setLastActivity();
  scheduleIdleTimeout();
}

function handleStorageEvent(event: StorageEvent): void {
  if (!isAuthenticated()) {
    return;
  }

  if (event.key === LAST_ACTIVITY_KEY) {
    scheduleIdleTimeout();
    return;
  }

  if (event.key === LOGOUT_EVENT_KEY && event.newValue) {
    void triggerIdleLogout(false);
  }
}

function handleVisibilityChange(): void {
  if (document.hidden) {
    return;
  }

  scheduleIdleTimeout();
}

export function startIdleLogout(): void {
  if (isStarted) {
    return;
  }

  isStarted = true;

  ACTIVITY_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, handleUserActivity);
  });

  window.addEventListener("storage", handleStorageEvent);
  document.addEventListener("visibilitychange", handleVisibilityChange);

  stopAuthWatch = store.watch(
    (_state, getters) => getters.authenticated,
    (authenticated) => {
      if (authenticated) {
        setLastActivity();
        scheduleIdleTimeout();
        return;
      }

      clearIdleTimeout();
      localStorage.removeItem(LAST_ACTIVITY_KEY);
    }
  );

  if (isAuthenticated()) {
    setLastActivity();
    scheduleIdleTimeout();
  }
}

export function stopIdleLogout(): void {
  if (!isStarted) {
    return;
  }

  isStarted = false;

  ACTIVITY_EVENTS.forEach((eventName) => {
    window.removeEventListener(eventName, handleUserActivity);
  });

  window.removeEventListener("storage", handleStorageEvent);
  document.removeEventListener("visibilitychange", handleVisibilityChange);

  if (stopAuthWatch) {
    stopAuthWatch();
    stopAuthWatch = null;
  }

  clearIdleTimeout();
}

export function consumeIdleLogoutAlert(): boolean {
  const shouldShowAlert = sessionStorage.getItem(IDLE_LOGOUT_ALERT_KEY) === "true";

  if (shouldShowAlert) {
    sessionStorage.removeItem(IDLE_LOGOUT_ALERT_KEY);
  }

  return shouldShowAlert;
}
